import { SkeletonBox } from "@web/components/skeleton";
import { surfaceBase } from "@web/components/ui";
import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/contexts";
import { getCurrentOrg, getOrgCompetitions } from "@/features/organizations/api";

interface OrgCompetition {
  id: string;
  name: string;
  type: string;
  startsAt: string;
  endsAt: string;
  memberCount: number;
  createdAt: string;
}

export default function OrgCompetitions() {
  const toast = useToast();
  const [competitions, setCompetitions] = useState<OrgCompetition[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCompetitions = useCallback(async () => {
    const { data: org } = await getCurrentOrg();
    if (!org) {
      setLoading(false);
      return;
    }
    const { data, error } = await getOrgCompetitions(org.id);
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else if (data) {
      setCompetitions(data);
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    loadCompetitions();
  }, [loadCompetitions]);

  const now = new Date().toISOString();

  return (
    <div className="flex flex-col gap-4">
      {loading && (
        <div className="animate-pulse flex flex-col gap-2">
          {Array.from({ length: 3 }).map(() => {
            const key = crypto.randomUUID();
            return <SkeletonBox key={key} width="100%" height={80} borderRadius={20} />;
          })}
        </div>
      )}

      {!loading && competitions.length === 0 && (
        <div className="text-text-muted text-sm text-center py-8">
          No competitions in this organization yet.
        </div>
      )}

      {!loading &&
        competitions.map((comp) => {
          const isActive = comp.endsAt >= now;
          return (
            <div key={comp.id} className={`${surfaceBase} bg-card p-4 flex flex-col gap-2`}>
              <div className="flex items-center justify-between">
                <span className="text-text text-[1rem] font-black">{comp.name}</span>
                <span
                  className={`text-[0.625rem] font-bold uppercase tracking-[0.05em] px-2 py-0.5 rounded-full ${
                    isActive
                      ? "bg-[rgba(32,191,107,0.1)] text-[#20bf6b]"
                      : "bg-[rgba(0,0,0,0.04)] text-text-muted"
                  }`}
                >
                  {isActive ? "Active" : "Ended"}
                </span>
              </div>
              <div className="flex items-center gap-4 text-text-dim text-[0.75rem] font-bold">
                <span>{comp.memberCount} members</span>
                <span>
                  {comp.startsAt} &rarr; {comp.endsAt}
                </span>
              </div>
            </div>
          );
        })}
    </div>
  );
}
